import {put, call, takeLatest, select} from 'redux-saga/effects';
import ReviewsApi from '../api/reviews.api';
import * as ActionTypes from '../actions/types.action';
import {getBearerHeader} from '../helpers/api.helper';
import {GoogleAnalyticsHelper} from '../helpers/googleAnalytics.helper';
import {
  getReviewError,
  getReviewFailureReason,
} from '../helpers/orderFeedback.helper';
import {ANALYTICS_EVENTS} from '../constants/constants';

// Shared by the product page's "Leave a review" and the order feedback popup.
// The server takes the customer from the token and the order from their
// latest one containing the product. A rating alone is a review, so the
// comment is sent only when there is one.
export const buildReviewVariables = ({itemId, rating, comment}) => ({
  itemId,
  rating,
  ...(comment ? {body: comment} : {}),
});

// Product page review analytics: the item and rating, whether there was a
// comment, and on failure a fixed reason code. Never the comment or the
// server's message.
const trackProductReview = (event, action, extra) =>
  GoogleAnalyticsHelper.trackEvent(event, {
    itemId: action.itemId,
    rating: action.rating,
    commentCount: action.comment ? 1 : 0,
    ...extra,
  });

// `message` is the gateway's GraphQL error, shown as is (a recent review's
// says when the product can be reviewed again); `code` says whether the
// product is locked.
function* failReview(action, errors) {
  const {message, code} = getReviewError(errors);
  trackProductReview(ANALYTICS_EVENTS.PRODUCT_REVIEW_SUBMIT_FAILED, action, {
    reason: getReviewFailureReason(code),
  });
  yield put({
    type: ActionTypes.SUBMIT_REVIEW_FAILED,
    message,
    code,
    itemId: action.itemId,
  });
}

function* submitReview(action) {
  trackProductReview(ANALYTICS_EVENTS.PRODUCT_REVIEW_SUBMIT, action);
  try {
    const storeToken = yield select(state => state.auth);
    const headers = getBearerHeader(null, storeToken);
    if (!headers) {
      yield put({type: ActionTypes.ERROR_REQUIRE_LOGIN});
      return;
    }

    const variables = buildReviewVariables(action);

    const result = yield call(ReviewsApi.submitReview, variables, headers);
    if (result && result.errors && result.errors.length > 0) {
      yield call(failReview, action, result.errors);
    } else {
      trackProductReview(
        ANALYTICS_EVENTS.PRODUCT_REVIEW_SUBMIT_SUCCESS,
        action
      );
      yield put({
        type: ActionTypes.SUBMIT_REVIEW_SUCCESS,
        result,
        itemId: action.itemId,
      });
    }
  } catch (e) {
    // The gateway's GraphQL error when there is one; otherwise none, and
    // the review modal shows its own copy rather than an HTTP error.
    yield call(
      failReview,
      action,
      e.response && e.response.data && e.response.data.errors
    );
  }
}

export default function* reviewsSaga() {
  yield takeLatest(ActionTypes.SUBMIT_REVIEW_REQUESTED, submitReview);
}
